import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getPosts, getLikes, toggleLike as apiToggleLike, getUserPurchases, createPostPurchaseCheckout, reorderPosts, getPaymentTiers } from '../services/api';
import { Heart, MessageSquare, Lock, Search, Image as ImageIcon, Video, Clock, FolderOpen, DollarSign, ShoppingCart, Loader2, Play, Camera, Film, Gift, GripVertical, Pencil } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { PageWrapper, staggerContainer, staggerItem } from '../components/animations';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FeedPage = () => {
  const { currentUser, isOwner, isModerator, isReviewer } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [likesData, setLikesData] = useState({});
  const [userLikes, setUserLikes] = useState({});
  const [userPurchases, setUserPurchases] = useState({ purchased_posts: [], purchased_collections: [] });
  const [purchasingPostId, setPurchasingPostId] = useState(null);
  const [tierNames, setTierNames] = useState({});

  const loadUserPurchases = useCallback(async () => {
    try {
      const purchases = await getUserPurchases();
      setUserPurchases(purchases);
    } catch (error) {
    }
  }, []);

  const loadTierNames = useCallback(async () => {
    try {
      const data = await getPaymentTiers();
      if (data?.tiers) {
        const names = {};
        for (const [, tier] of Object.entries(data.tiers)) {
          names[tier.tier_level ?? 0] = tier.name || 'Unknown';
        }
        setTierNames(names);
      }
    } catch (error) {
    }
  }, []);

  const handleBuyPost = async (post, e) => {
    e.stopPropagation();
    
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      setPurchasingPostId(post.id);
      const result = await createPostPurchaseCheckout(post.id);
      // Redirect to Stripe checkout
      window.location.href = result.checkout_url;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to initiate purchase',
        variant: 'destructive'
      });
      setPurchasingPostId(null);
    }
  };

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPosts(null, false);
      setPosts(data);
      setFilteredPosts(data);
      if (isReviewer) return;
      
      // Load likes for each post
      const likesPromises = data.map(post => 
        getLikes(post.id).then(result => ({ postId: post.id, ...result }))
      );
      const likesResults = await Promise.all(likesPromises);
      
      const likesMap = {};
      const userLikesMap = {};
      likesResults.forEach(result => {
        likesMap[result.postId] = result.count;
        userLikesMap[result.postId] = result.likes.some(like => like.userId === currentUser?.id);
      });
      
      setLikesData(likesMap);
      setUserLikes(userLikesMap);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, isReviewer]);

  useEffect(() => {
    loadPosts();
    loadUserPurchases();
    loadTierNames();
  }, [loadPosts, loadUserPurchases, loadTierNames]);

  const handleLike = async (postId, e) => {
    e.stopPropagation();
    try {
      const result = await apiToggleLike(postId);
      setUserLikes(prev => ({ ...prev, [postId]: result.liked }));
      setLikesData(prev => ({
        ...prev,
        [postId]: result.liked ? (prev[postId] || 0) + 1 : (prev[postId] || 1) - 1
      }));
    } catch (error) {
    }
  };

  useEffect(() => {
    let filtered = posts.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.content.toLowerCase().includes(searchQuery.toLowerCase());
      
      let matchesFilter = true;
      if (filterType === 'photos') {
        matchesFilter = post.postType === 'Photos';
      } else if (filterType === 'videos') {
        matchesFilter = post.postType === 'Videos';
      } else if (filterType === 'bonus') {
        matchesFilter = post.postType === 'Bonus';
      } else {
        // "all" tab: exclude Bonus posts AND archived posts (unless featured)
        matchesFilter = post.postType !== 'Bonus' && (!post.archived || post.featured);
      }

      return matchesSearch && matchesFilter;
    });

    // Sort by custom sort_order for the active section, fallback to date
    const orderKey = filterType === 'bonus' ? 'sort_order_bonus' : 'sort_order_feed';
    filtered.sort((a, b) => {
      // Featured posts always float to top
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      // Then custom sort order
      const aOrder = a[orderKey] != null ? a[orderKey] : 999999;
      const bOrder = b[orderKey] != null ? b[orderKey] : 999999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return new Date(b.publishedAt) - new Date(a.publishedAt);
    });
    
    setFilteredPosts(filtered);
  }, [searchQuery, filterType, posts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const section = filterType === 'bonus' ? 'bonus' : 'feed';
    
    setFilteredPosts(prev => {
      const oldIndex = prev.findIndex(p => p.id === active.id);
      const newIndex = prev.findIndex(p => p.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      reorderPosts(section, reordered.map(p => p.id)).catch(() => {
        toast({ title: 'Error', description: 'Failed to save order', variant: 'destructive' });
      });
      return reordered;
    });
  }, [filterType]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Ensure UTC parsing - backend sends naive UTC dates without Z suffix
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const TIER_COLORS = { 0: 'bg-gray-700 border-gray-600 text-gray-300', 1: 'bg-orange-900/80 border-orange-700 text-orange-300', 2: 'bg-slate-600/80 border-slate-400 text-slate-200', 3: 'bg-amber-800/80 border-amber-600 text-amber-300' };

  const PostCard = ({ post }) => {
    const likeCount = likesData[post.id] || 0;
    const isLiked = userLikes[post.id] || false;
    const isPaidPost = post.isForSale && post.price > 0;
    const userOwnsPost = userPurchases.purchased_posts?.includes(post.id);
    const canViewForFree = isOwner || isModerator || userOwnsPost;
    const isLocked = post.locked;

    return (
      <Card 
        className={`overflow-hidden transition-all cursor-pointer group bg-gray-900/90 border-2 flex flex-col h-full ${isLocked ? 'border-amber-600/40 hover:border-amber-500/60' : 'border-amber-900/30 hover:border-amber-700/50'} hover:shadow-[0_0_20px_rgba(251,191,36,0.2)]`}
        onClick={() => navigate(`/post/${post.id}`)}
        data-testid={`post-card-${post.id}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          {post.images && post.images.length > 0 ? (
            <img 
              src={post.images[0]} 
              alt={post.title}
              className={`w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300 ${isLocked ? 'grayscale blur-sm' : ''}`}
              style={{filter: isLocked ? 'blur(8px) grayscale(0.8)' : 'sepia(0.2) contrast(1.05)'}}
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${isLocked ? 'bg-gray-800' : 'bg-gray-900'}`}>
              <Lock className={`w-12 h-12 ${isLocked ? 'text-amber-600/60' : 'text-gray-700'}`} />
            </div>
          )}
          {post.featured && !isLocked && (
            <Badge className={`absolute top-2 bg-red-700 border-red-600 font-headline tracking-wider ${isOwner ? 'right-11' : 'right-2'}`}>
              FEATURED
            </Badge>
          )}
          {!post.featured && post.archived && isOwner && (
            <Badge className="absolute top-2 right-11 bg-gray-700 border-gray-600 font-headline tracking-wider text-gray-300">
              ARCHIVED
            </Badge>
          )}
          {/* Case file tab */}
          <div className="absolute top-2 left-2 bg-amber-700/90 text-black px-3 py-1 text-xs font-headline tracking-wider">
            CASE #{post.id?.slice(-4) || '0001'}
          </div>
          {/* Owner edit button */}
          {isOwner && (
            <button
              className="absolute top-2 right-2 z-20 bg-amber-700/90 text-black p-1.5 rounded hover:bg-amber-600 transition-colors"
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/posts/${post.id}/edit`); }}
              data-testid={`edit-post-btn-${post.id}`}
              title="Edit this post"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          {/* Tier badge */}
          {(isOwner || isReviewer) && tierNames[post.minimumTierLevel] && (
            <Badge className={`absolute bottom-2 left-2 text-[10px] font-headline tracking-wider ${TIER_COLORS[post.minimumTierLevel] || 'bg-gray-700 border-gray-600 text-gray-300'}`} data-testid={`tier-badge-${post.id}`}>
              {tierNames[post.minimumTierLevel]}
            </Badge>
          )}
          {/* Locked overlay */}
          {isLocked && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
              <Lock className="w-10 h-10 text-amber-500 mb-2" />
              <span className="text-amber-400 font-headline text-sm tracking-wider">
                {tierNames[post.minimumTierLevel] || 'PREMIUM'} ONLY
              </span>
            </div>
          )}
          {/* Video play indicator */}
          {!isLocked && post.vimeoEmbedUrls && post.vimeoEmbedUrls.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-14 h-14 rounded-full bg-amber-600/90 flex items-center justify-center shadow-lg">
                <Play className="w-7 h-7 text-black ml-1" fill="currentColor" />
              </div>
            </div>
          )}
          {post.previewMode && post.hasVideo && (
            <div className="absolute bottom-2 right-2 bg-black/70 border border-amber-700/50 text-amber-400 px-2 py-1 text-[10px] font-headline tracking-wider flex items-center gap-1" data-testid={`video-chip-${post.id}`}>
              <Film className="w-3 h-3" /> VIDEO
            </div>
          )}
          {/* Price badge for paid posts */}
          {isPaidPost && !canViewForFree && !isLocked && (
            <div className="absolute bottom-2 right-2 bg-green-600 text-white px-3 py-1 text-sm font-headline tracking-wider flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              {post.price?.toFixed(2)}
            </div>
          )}
          {userOwnsPost && (
            <div className="absolute bottom-2 right-2 bg-amber-600 text-black px-3 py-1 text-xs font-headline tracking-wider">
              PURCHASED
            </div>
          )}
        </div>
        
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border-2 border-amber-700/50">
                <AvatarImage src={post.authorAvatar || currentUser?.avatarUrl} />
                <AvatarFallback className="bg-amber-900/50 text-amber-500">{(post.authorName || 'D')[0]}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium text-amber-500 font-headline tracking-wide">{post.authorName || 'Daria'}</p>
                <p className="text-xs text-gray-500 font-typewriter">{formatDate(post.publishedAt)}</p>
              </div>
            </div>
            {isLocked && (
              <Badge className="bg-amber-600/20 text-amber-400 border border-amber-600/40 font-headline text-xs tracking-wider">
                <Lock className="w-3 h-3 mr-1" /> LOCKED
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="pb-3 flex-1">
          <h3 className="text-xl font-bold mb-2 group-hover:text-amber-500 transition-colors font-headline tracking-wide text-gray-100">
            {post.title}
          </h3>
          {isLocked ? (
            <p className="text-amber-500/70 font-typewriter text-sm italic">
              Upgrade to {tierNames[post.minimumTierLevel] || 'a higher tier'} to unlock this case file...
            </p>
          ) : (
            <p className="text-gray-400 line-clamp-2 font-typewriter text-sm">
              {post.content}
            </p>
          )}
        </CardContent>

        <CardFooter className="pt-3 border-t border-amber-900/30 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {isReviewer ? (
              <span className="text-xs text-gray-500 font-typewriter">{post.imageCount > 1 ? `${post.imageCount} photos` : ''}</span>
            ) : (
            <>
            <button 
              className="flex items-center gap-1 hover:text-red-500 transition-colors"
              onClick={(e) => handleLike(post.id, e)}
              data-testid={`like-btn-${post.id}`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
              <span className="text-sm text-gray-400">{likeCount}</span>
            </button>
            <div className="flex items-center gap-1 text-gray-500">
              <MessageSquare className="w-5 h-5" />
              <span className="text-sm text-gray-400">{post.commentCount || 0}</span>
            </div>
            </>
            )}
          </div>
          {isPaidPost && !canViewForFree ? (
            <Button 
              onClick={(e) => handleBuyPost(post, e)}
              disabled={purchasingPostId === post.id}
              className="bg-green-600 hover:bg-green-700 text-white font-headline tracking-wider"
              data-testid={`buy-post-btn-${post.id}`}
            >
              {purchasingPostId === post.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShoppingCart className="w-4 h-4 mr-2" />
              )}
              BUY ${post.price?.toFixed(2)}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20 font-headline tracking-wider">
              OPEN FILE
            </Button>
          )}
        </CardFooter>
      </Card>
    );
  };

  const SortablePostCard = ({ post }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: post.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 10 : 'auto',
    };
    return (
      <div ref={setNodeRef} style={style} className="relative">
        {isOwner && (
          <div {...attributes} {...listeners} className="absolute top-3 right-14 z-20 bg-amber-700/90 text-black p-1.5 rounded cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${post.id}`}>
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        <PostCard post={post} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black relative">
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        <div className="container mx-auto px-4 py-16 text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-amber-500/70 font-typewriter">Retrieving case files...</p>
        </div>
      </div>
    );
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-black relative" data-testid="feed-page">
        {/* Texture overlay */}
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        
        <div className="container mx-auto px-4 py-8 max-w-6xl relative z-10">
          {/* Header */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block mb-4">
              <div className="bg-amber-900/20 border-2 border-amber-600 px-6 py-2 inline-flex items-center gap-3 animate-glow-pulse">
                <FolderOpen className="w-6 h-6 text-amber-500" />
                <h1 className="text-3xl font-headline text-amber-500 tracking-wider" data-testid="feed-title">POSTS</h1>
              </div>
            </div>
            <p className="text-gray-400 font-typewriter">Explore exclusive content based on your clearance level</p>
          </motion.div>

          {/* Search and Filters */}
          <motion.div 
            className="mb-6 space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-600 w-5 h-5" />
            <Input
              placeholder="Search case files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-900/80 border-amber-900/50 focus:border-amber-600 font-typewriter"
              data-testid="search-input"
            />
          </div>

          <Tabs value={filterType} onValueChange={setFilterType}>
            <TabsList className="grid w-full grid-cols-4 bg-gray-900/80 border border-amber-900/30">
              <TabsTrigger value="all" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">All</TabsTrigger>
              <TabsTrigger value="photos" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
                <Camera className="w-4 h-4 mr-2" />
                Photos
              </TabsTrigger>
              <TabsTrigger value="videos" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
                <Film className="w-4 h-4 mr-2" />
                Videos
              </TabsTrigger>
              <TabsTrigger value="bonus" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
                <Gift className="w-4 h-4 mr-2" />
                Bonus
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {/* Posts Grid */}
        {isOwner ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredPosts.map(p => p.id)} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPosts.map(post => (
                  <SortablePostCard key={post.id} post={post} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                variants={staggerItem}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
              >
                <PostCard post={post} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {filteredPosts.length === 0 && (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <FolderOpen className="w-16 h-16 mx-auto text-amber-900/50 mb-4" />
            <p className="text-amber-500/70 text-lg font-typewriter">
              {posts.length === 0 ? 'No case files available yet' : 'No matching case files found'}
            </p>
          </motion.div>
        )}
      </div>
    </div>
    </PageWrapper>
  );
};

export default FeedPage;